import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { PageHero, Section, CtaBand, SectionHead } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { GoldButton, WhatsAppButtonLight } from "@/components/cta";
import { Check, Wallet, Server } from "lucide-react";

const packages = [
  { name: "Launch Page", desc: "One-page site to get you online fast", price: "2,200" },
  { name: "Business Essential", desc: "A full multi-page site for your business", price: "4,500", featured: true },
  { name: "School / Hotel", desc: "Presence plus enquiries, bookings or admissions", price: "6,000" },
  { name: "Shop & Pay", desc: "Online shop with Mobile Money & card checkout", price: "9,500" },
  { name: "Custom Portal / App", desc: "Logins, dashboards & bespoke tools", price: "14,000" },
];

const milestones = [
  { pct: "20%", label: "to book your sprint" },
  { pct: "30%", label: "when scope is agreed" },
  { pct: "30%", label: "when your draft is ready to review" },
  { pct: "20%", label: "before we go live" },
];

const hosting = [
  { tier: "Website hosting", price: "300" },
  { tier: "Business & Shop", price: "700" },
  { tier: "Application", price: "1,200" },
];

export default function Pricing() {
  return (
    <>
      <Seo
        title="Website Prices in Ghana (from GHS 2,200) — Sankofa Launch Studio"
        description="Simple, clear website packages in Ghana Cedis: launch pages from GHS 2,200, business sites from GHS 4,500, school/hotel from GHS 6,000, online shops from GHS 9,500, custom apps from GHS 14,000. Fixed prices, milestone payments, managed hosting from GHS 300/month."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]),
        ]}
      />
      <PageHero
        eyebrow="Packages & Pricing"
        breadcrumb="Home / Pricing"
        title="Simple, clear packages in Ghana Cedis"
        intro="You know the cost before we start. Add-ons available. All prices are fixed and agreed before any work begins."
      />

      <Section className="bg-cream">
        <StaggerGroup className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => (
            <StaggerItem key={p.name}>
              <article
                data-testid={`pricing-card-${p.name.split(" ")[0].toLowerCase()}`}
                className={`flex h-full flex-col rounded-2xl p-8 shadow-soft transition-transform duration-300 hover:-translate-y-1 ${
                  p.featured ? "bg-navy text-white ring-2 ring-gold" : "bg-white"
                }`}
              >
                {p.featured && (
                  <span className="mb-4 inline-flex w-fit rounded-full bg-gold px-3 py-1 text-xs font-600 uppercase tracking-wide text-navy-dark">
                    Most popular
                  </span>
                )}
                <h3 className={`font-serif text-2xl font-600 ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${p.featured ? "text-white/75" : "text-mutedink"}`}>{p.desc}</p>
                <div className="mt-6">
                  <span className={`text-xs uppercase tracking-wide ${p.featured ? "text-white/60" : "text-mutedink"}`}>from</span>
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-2xl font-600 text-gold">GHS</span>
                    <span className="font-serif text-4xl font-700 text-gold">{p.price}</span>
                  </div>
                </div>
                <div className="mt-auto pt-7">
                  <GoldButton to="/contact" testid={`pricing-quote-${p.name.split(" ")[0].toLowerCase()}`} className="w-full" icon={false}>
                    Get a fixed quote
                  </GoldButton>
                </div>
              </article>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </Section>

      {/* Milestone payments */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
            <Reveal>
              <div className="h-full rounded-2xl bg-cream p-8 md:p-10" data-testid="milestone-block">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white">
                    <Wallet className="h-6 w-6" />
                  </span>
                  <h2 className="font-serif text-2xl font-600 text-navy md:text-3xl">Payments are simple and fair</h2>
                </div>
                <p className="mt-4 text-ink">
                  You pay in stages as your project moves forward — never everything up front.
                </p>
                <ul className="mt-6 space-y-3">
                  {milestones.map((m, i) => (
                    <li key={i} className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-soft">
                      <span className="font-serif text-2xl font-700 text-gold">{m.pct}</span>
                      <span className="text-sm text-ink">{m.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="h-full rounded-2xl bg-cream p-8 md:p-10" data-testid="hosting-block">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white">
                    <Server className="h-6 w-6" />
                  </span>
                  <h2 className="font-serif text-2xl font-600 text-navy md:text-3xl">We keep it running for you</h2>
                </div>
                <p className="mt-4 leading-relaxed text-ink">
                  Your website hosting is paid in advance. As long as your hosting plan is active, we keep your website
                  online, monitored and technically maintained. If it isn't renewed, the site is paused until payment is received.
                </p>
                <ul className="mt-6 space-y-3">
                  {hosting.map((h) => (
                    <li key={h.tier} className="flex items-center justify-between rounded-xl bg-white p-4 shadow-soft">
                      <span className="text-sm text-ink">{h.tier}</span>
                      <span className="font-serif font-700 text-gold">from GHS {h.price}/mo</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-mutedink">
                  Pay monthly or save with annual — about two months free.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand title="Get a fixed quote" text="Tell us what you need and we'll send a clear, fixed price in Ghana Cedis." />
    </>
  );
}
